// צורות התשובה של /api/artifacts*, /api/runs*, /api/panel/* כפי שהמסך צורך אותן (09 §1, 10 §1).
// 09 אימץ את הצורות האלה כחוזה (specs/09 §1 "גופי התשובה"); apps/api מגיש אותן, apps/api/test/artifacts.test.ts
// מאמת מולן, וה-mock (apps/web/mock) מגיש בדיוק אותן. נבדקות ב-zod בצד הלקוח.
import { z } from 'zod';
import { ArtifactKind, ArtifactRunStatus, ArtifactStatus, Role, RunAs } from '@aibify/core';

export const UserRef = z.object({ id: z.string(), nameHe: z.string() });
export type UserRef = z.infer<typeof UserRef>;

const IsoTs = z.string();

export const RunSummary = z.object({
  id: z.string(),
  status: ArtifactRunStatus,
  version: z.number().int(),
  startedAt: IsoTs.nullable(),
  finishedAt: IsoTs.nullable(),
  asOf: IsoTs.nullable(),
  user: UserRef,
  runAs: UserRef,
  /** 19: הריצה הופעלה מתזמון (null = ידנית). */
  trigger: z.object({ id: z.string(), nameHe: z.string(), attempt: z.number().optional() }).nullable().optional(),
  /** artifacts-report-lookup: ברשימת הריצות — הפרמטרים והתוויות של שדות בחירה עם חיפוש ("לקוח: <תווית>"). */
  params: z.unknown().optional(),
  paramLabels: z.record(z.string(), z.string()).nullable().optional(),
});
export type RunSummary = z.infer<typeof RunSummary>;

/** 09 §1.1: עם מי היוצר שיתף (כל המופע — מסונן לפי minRole; משתמשים מסוימים). שיתוף = צפייה + הרצה בלבד. */
export const ArtifactShares = z.object({
  everyone: z.boolean(),
  users: z.array(z.object({ id: z.string(), nameHe: z.string(), email: z.string().optional(), sharedBy: z.object({ id: z.string(), nameHe: z.string() }).optional() })),
});
export type ArtifactShares = z.infer<typeof ArtifactShares>;

export const ArtifactSummary = z.object({
  slug: z.string(),
  nameHe: z.string(),
  descriptionHe: z.string(),
  /** 22: הסוג של הגרסה החיה — תג "דוח" / "שיחה" / "אפליקציה". */
  kind: ArtifactKind,
  /** 22 §1.6: גרסת הסוג שהגרסה החיה (או האחרונה) בנויה עליה; null — אין גרסאות. */
  kindVersion: z.number().int().nullable().default(null),
  /** הגרסה העדכנית של הסוג בפלטפורמה — גדולה מ-kindVersion = "שדרוג זמין" (שלב 3). */
  latestKindVersion: z.number().int().nullable().default(null),
  /** 22 §1.7 + 25 §1.5 — ליוצר בלבד: יש גרסת סוג חדשה או רכיב מוכן שהתעדכן — תווית אחת "שדרוג זמין", בלי פירוט. */
  upgradeAvailable: z.boolean().optional(),
  status: ArtifactStatus,
  runAs: RunAs,
  minRole: Role,
  currentVersion: z.number().int().nullable(),
  creator: UserRef,
  lastRun: RunSummary.pick({ id: true, status: true, asOf: true, finishedAt: true }).nullable(),
  /** artifacts-list-dates: "נוצר" ו"עודכן" — השינוי האחרון ביצירה עצמה (גרסה, פרסום, שם), לא ריצה ולא "נכון ל-". */
  createdAt: z.string().nullable().default(null),
  updatedAt: z.string().nullable().default(null),
  /** 19: מספר התזמונים על היצירה (צ'יפ "מתוזמן"/"מושהה" בכרטיס). */
  triggers: z.object({ active: z.number().int(), paused: z.number().int() }).default({ active: 0, paused: 0 }),
  /** 09 §1.1: ליוצר — עם מי שיתף (ריק = "פרטי"). */
  shared: ArtifactShares.optional(),
  /** 09 §1.1: למי שאינו היוצר — מי שיתף איתו ("משותף איתי · <שם>"). */
  sharedBy: UserRef.nullable().optional(),
});
export type ArtifactSummary = z.infer<typeof ArtifactSummary>;

/** 22 §1.5: תוצאת הבנייה של גרסת app (build.json) — שלבים לפי הסדר, זמן, וזנב הפלט של שלב שנכשל. */
export const ArtifactBuild = z.object({
  ok: z.boolean(),
  steps: z.array(z.object({ name: z.string(), ok: z.boolean(), ms: z.number(), tail: z.string().nullable().optional() })),
  builtAt: IsoTs.nullable().optional(),
});
export type ArtifactBuild = z.infer<typeof ArtifactBuild>;

/** GET /api/artifacts */
export const ArtifactListResponse = z.object({ artifacts: z.array(ArtifactSummary) });
export type ArtifactListResponse = z.infer<typeof ArtifactListResponse>;

export const ArtifactVersionSummary = z.object({
  version: z.number().int(),
  noteHe: z.string().nullable(),
  createdAt: IsoTs,
  createdBy: UserRef,
  /** 22 §1.6: הסוג וגרסת הסוג נעוצים לגרסה. */
  kind: ArtifactKind.optional(),
  kindVersion: z.number().int().optional(),
});

/** GET /api/artifacts/:slug (+גרסאות; paramsSchema של הגרסה הנוכחית) */
export const ArtifactDetailResponse = z.object({
  artifact: ArtifactSummary,
  /** יצירת chat לשדה "שאל" (מעל פריסה שהיצירה הזו משתמשת בה; ליצירת chat — היא עצמה); null = אין שדה "שאל". */
  ask: z.object({ slug: z.string(), nameHe: z.string() }).nullable().default(null),
  versions: z.array(ArtifactVersionSummary),
  paramsSchema: z.record(z.string(), z.unknown()),
  /** 09 §1.1: היוצר בלבד (גם לא admin) — עריכה, גרסאות, ממשקים ושיתוף. */
  canEdit: z.boolean().optional(),
  /** 22 §1.7: הסוג וגרסתו של הגרסה המוצגת; latestKindVersion — "זמינה n". */
  kind: ArtifactKind.optional(),
  kindVersion: z.number().int().nullable().optional(),
  latestKindVersion: z.number().int().nullable().optional(),
  /** SPEC.md של app ("מה הכלי עושה" — גם לשותף); null ל-report/chat. */
  spec: z.string().nullable().optional(),
  /** תוצאת הבנייה של app — ליוצר בלבד; null ל-report/chat. */
  build: ArtifactBuild.nullable().optional(),
  /** 22 §1.4: קובצי המקור של הגרסה האחרונה — היוצר בלבד (הורדה: GET /api/artifacts/:slug/files/*path). מוגש ב-detail של גרסת app (שלב 2). */
  files: z.array(z.object({ path: z.string(), bytes: z.number().int() })).optional(),
  /** 25 §1.5 — ליוצר: הרכיבים המוכנים שהגרסה בנויה עליהם (לאייבי; המסך לא מציג שמות). */
  core: z.array(z.object({ key: z.string(), version: z.number().int() })).optional(),
  /** 22 §1.7 + 25 §1.5 — ליוצר: מה שאפשר לשדרג (סוג / רכיב מוכן); המסך מציג רק "שדרוג זמין". */
  upgrades: z.array(z.object({ kind: ArtifactKind.optional(), core: z.string().optional(), from: z.number().int(), to: z.number().int() })).optional(),
  /** artifacts-draft-preview: ?version=<n> (היוצר בלבד) — הגרסה המוצגת אינה החיה; draft = מעל החיה (או שאין חיה). */
  preview: z.object({ version: z.number().int(), draft: z.boolean() }).optional(),
});
export type ArtifactDetailResponse = z.infer<typeof ArtifactDetailResponse>;

/** GET/PUT /api/artifacts/:slug/share (היוצר בלבד) — המצב; ב-GET גם candidates (משתמשים פעילים במופע חוץ מהיוצר). */
export const ShareResponse = z.object({
  slug: z.string(),
  shared: ArtifactShares,
  candidates: z.array(z.object({ id: z.string(), nameHe: z.string(), email: z.string(), role: Role })).optional(),
  /** data-access-rls §7.1: לכל משתמש מוגבל (בלבד) הערה מהשרת, למשל "רואה רק את הנתונים שהוקצו לו" או "הדוח ייכשל אצלו: …". */
  dataAccess: z.record(z.string(), z.object({ noteHe: z.string() })).optional(),
});
export type ShareResponse = z.infer<typeof ShareResponse>;
/** artifacts-talk-to-aibi: GET/POST /api/artifacts/:slug/aibi-conversation — השיחה של הצופה עצמו, או null */
export const AibiConversationResponse = z.object({ conversationId: z.string().nullable() });

/** POST /api/artifacts/:slug/archive (היוצר בלבד; artifacts-09-archive) — בארכיון היצירה לא מוצגת בשום מקום; שחזור דרך אייבי. */
export const ArchiveResponse = z.object({ slug: z.string(), nameHe: z.string(), status: z.literal('archived') });
export type ArchiveResponse = z.infer<typeof ArchiveResponse>;
// artifacts-rename: PATCH /api/artifacts/:slug — שם/תיאור (מטא-דאטה, בלי גרסה)
export const RenameResponse = z.object({ slug: z.string(), nameHe: z.string(), descriptionHe: z.string() });
export type RenameResponse = z.infer<typeof RenameResponse>;

/** POST /api/artifacts/:slug/publish {version} (היוצר בלבד) — הגרסה נעשית החיה; פרסום ≠ שיתוף (artifacts-draft-preview: "פרסם" במסך). */
export const PublishResponse = z.object({ slug: z.string(), currentVersion: z.number().int() });
export type PublishResponse = z.infer<typeof PublishResponse>;

/** GET /api/artifacts/:slug/runs — ריצות (הכי חדשה ראשונה; מי ששותף רואה את שלו, היוצר את כולן — ל-admin אין חריגה). */
export const RunListResponse = z.object({ runs: z.array(RunSummary) });
export type RunListResponse = z.infer<typeof RunListResponse>;

/** POST /api/artifacts/:slug/runs {params, paramLabels?, version?} → {runId}. version — ריצת טיוטה בתצוגה מקדימה (היוצר בלבד). paramLabels — רק לפרמטרים עם lookup (artifacts-report-lookup). */
export const CreateRunResponse = z.object({ runId: z.string() });

/** POST /api/artifacts/:slug/lookup/:param {q, version?} (report@3; version — טיוטה בתצוגה מקדימה): עד 50 שורות; truncated — יש עוד. 404 no_lookup / 422 lookup_bad_result / 429. */
export const LookupResponse = z.object({
  rows: z.array(z.object({ value: z.union([z.string(), z.number()]), labelHe: z.string(), hintHe: z.string().optional() })),
  truncated: z.boolean(),
});
export type LookupResponse = z.infer<typeof LookupResponse>;

/** GET /api/runs/:id — output הוא Screen לא מאומת כאן: @aibify/display מאמת בלוק-בלוק (לא-מוכר → raw). */
export const RunDetail = RunSummary.extend({
  artifactSlug: z.string(),
  params: z.unknown(),
  output: z.unknown().nullable(),
  log: z.string().nullable(),
  errorHe: z.string().nullable(),
  /** 20: הריצה נעצרה כי ל-runAs (הצופה) אין Gmail — המסך פותח "חיבור Gmail" ומריץ שוב אחרי החיבור. */
  mailConnect: z.object({ caps: z.array(z.enum(['send', 'read'])).min(1) }).nullable().optional(),
});
export type RunDetail = z.infer<typeof RunDetail>;
export const RunResponse = z.object({ run: RunDetail });

/**
 * GET /api/artifacts/:slug/runs/latest?params= (report@2, artifacts-report-cache): הריצה המוצלחת האחרונה במפתח של הקורא (המטמון).
 * stale — עבר זמן התוקף ויש נתונים חדשים; recomputing — ריצה שהשרת פתח ברקע (או שכבר חיה); noNewData — עבר זמן התוקף בלי נתונים חדשים;
 * external — לדוח יש פעולה חיצונית (מחושב רק בבקשה). log רק ליוצר. 404 not_cached_kind / no_cache → המסך רץ כמו היום.
 */
export const LatestRunResponse = z.object({
  run: RunDetail.extend({ log: z.string().nullable().optional() }),
  stale: z.boolean(),
  newDataSince: IsoTs.nullable(),
  noNewData: z.boolean(),
  recomputing: z.string().nullable(),
  ttlMin: z.number(),
  external: z.boolean(),
});
export type LatestRunResponse = z.infer<typeof LatestRunResponse>;

export const TERMINAL: ReadonlySet<string> = new Set(['done', 'failed', 'timeout', 'canceled', 'waiting_consent']);

/** גוף שגיאה אחיד של apps/api: { error: { code, textHe } }. המסך מציג רק textHe; kind = code (11 §4). */
export const ApiErrorBody = z.object({ error: z.object({ code: z.string(), textHe: z.string() }) });
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

/** GET /api/panel/ai1|ai2 (10 §1) — בשלד רק מוצג בקצרה. */
export const PanelAi2 = z.object({
  spreads: z.array(z.object({ key: z.string(), nameHe: z.string(), asOf: z.string().nullable() }).passthrough()),
  artifacts: z.array(z.object({ slug: z.string(), nameHe: z.string() }).passthrough()),
}).passthrough();
export const PanelAi1 = z.object({
  worlds: z.array(z.object({ key: z.string(), nameHe: z.string() }).passthrough()),
  pendingProposals: z.array(z.unknown()),
}).passthrough();
